import type { ApiRentalDealItem } from 'src/types/rental';

import { useQuery, queryOptions, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { requestSession } from 'src/utils/storefrontSession';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

/**
 * One rental deal, read under the account session — the one a renter opens from the email's
 * button, on a phone with no wallet (app#236). The api client sends the session's token with the
 * request; the read is keyed by that session, like the list (app#248).
 */
export const rentalDealQuery = (id: string, session: string) =>
  queryOptions({
    queryKey: QueryKeys.forSession(QueryKeys.rentalDeal(id), session),
    queryFn: async (): Promise<ApiRentalDealItem> => {
      const response = await axios.get<ApiRentalDealItem>(`/buyer/rental-deals/${id}`);
      return response.data;
    },
  });

export function useRental(id: string) {
  const session = useRequestSession();

  return useQuery({
    ...rentalDealQuery(id, session),
    staleTime: LIVE_STALE_TIME,
  });
}

/**
 * One fresh read of a rental deal, on demand, through the same query the screen reads — same cache
 * entry, same session key, same credentials. For the moments the pay dialog must know the booking
 * *now*: before creating the escrow contract, and after the factory said one already exists
 * (api#380, §2.9).
 */
export function useFetchRentalDeal() {
  const queryClient = useQueryClient();

  return (id: string): Promise<ApiRentalDealItem> =>
    queryClient.fetchQuery({ ...rentalDealQuery(id, requestSession()), staleTime: 0 });
}
